import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import Icon from '@/components/ui/Icon.jsx';
import { useDebouncedValue } from '@/hooks/useDebouncedValue.js';

/** Category pill with the backend-supplied colour dot (Liquidity, Risk, …). */
export function CategoryBadge({ label, color }) {
  if (!label || label === '—') return <span>—</span>;
  return (
    <span className="sg-cat">
      <span className="sg-cat-dot" style={{ background: color || 'var(--fg-subtle)' }} />
      {label}
    </span>
  );
}

export function TypeBadge({ label }) {
  if (!label || label === '—') return null;
  return <span className="sg-type">{label}</span>;
}

/**
 * Link chip for a person or organization. `kind` accepts both the list
 * shape ('person' / 'org') and the detail shape ('Person' / 'Organization').
 */
export function EntityChip({ id, name, kind, size }) {
  const isPerson = kind === 'person' || kind === 'Person';
  const className = `sg-chip${isPerson ? ' is-person' : ''}${size === 'lg' ? ' lg' : ''}`;
  if (!id) return <span className={className}>{name}</span>;
  return (
    <Link
      to={isPerson ? '/people/$id' : '/organizations/$id'}
      params={{ id }}
      className={className}
      title={name}
      onClick={(e) => e.stopPropagation()}
    >
      {name}
    </Link>
  );
}

export function SignalsError({ title, error, onRetry }) {
  return (
    <div className="empty-state">
      <Icon name="alert" size={36} stroke={1.5} />
      <h3>{title}</h3>
      <p>
        {error?.message || 'Something went wrong talking to the signals service.'}
        {error?.status ? ` (HTTP ${error.status})` : ''}
      </p>
      {onRetry && (
        <button type="button" className="btn btn-primary btn-md" onClick={onRetry}>
          <Icon name="refresh" size={14} /> Try again
        </button>
      )}
    </div>
  );
}

export function SignalsPending() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
      <span className="spinner" />
    </div>
  );
}

/** Breadcrumb: items are { label, to } — the last one renders as current. */
export function Crumbs({ items }) {
  return (
    <div className="sg-crumb">
      {items.map((it, i) => (
        <span key={i} style={{ display: 'contents' }}>
          {i > 0 && <span className="sep">/</span>}
          {it.to ? <Link to={it.to}>{it.label}</Link> : <span className="current">{it.label}</span>}
        </span>
      ))}
    </div>
  );
}

/** Native select styled to match the filter bar; '' means "any". */
export function FilterSelect({ value, onChange, placeholder, options }) {
  return (
    <select
      className={`sg-select${value ? '' : ' is-empty'}`}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      aria-label={placeholder}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

/**
 * Free-text keyword filter. Keeps its own draft so typing stays responsive,
 * and only reports upward once the user pauses (or presses Enter). Re-syncs
 * when `value` changes from outside, e.g. "Clear filters".
 */
export function KeywordFilter({ value, onChange, placeholder }) {
  const [draft, setDraft] = useState(value || '');
  const debounced = useDebouncedValue(draft, 350);

  useEffect(() => { setDraft(value || ''); }, [value]);

  useEffect(() => {
    if (debounced.trim() !== (value || '')) onChange(debounced.trim());
    // Only the debounced draft should trigger a report; `value` changing is
    // handled by the re-sync effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  return (
    <div className="cfield sg-filter-q">
      <div className="cfield-control">
        <Icon name="search" size={14} />
        <input
          placeholder={placeholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') onChange(draft.trim()); }}
        />
        {draft && (
          <button
            type="button"
            onClick={() => { setDraft(''); onChange(''); }}
            style={{ background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--fg-subtle)', display: 'flex', padding: 0 }}
            aria-label="Clear keyword"
          >
            <Icon name="x" size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
